import {
  useEffect, useMemo, useState, useCallback,
} from 'react';
import { useRouter as PagesRouter } from 'next/compat/router';
import { useExtendedRouter } from '@hooks/next-routing-wrappers';
import { useTranslation } from 'next-i18next';
import { useAuthClient } from '@hooks/common/useAuthClient/useAuthClient';
import {
  useSwrJobAddToBlocked,
  useSwrJobAddToFavorites,
  useSwrJobApply,
  useSwrJobRemoveFromBlocked,
  useSwrJobRemoveFromFavorites,
} from '@hooks/useSwrApi/endpoints/job';
import { buildGaApplyCookieValue, GA_JOB_APPLY_COOKIE_NAME, GA_JOB_APPLY_COOKIE_CONFIG } from '@utils/analytics/ga-cookie-helper';
import { useSwrBulkApplyFeed } from '@hooks/useSwrApi/endpoints/job-operations';
import { JobListType, JobPlacement } from '@type/job-params';
import { EasyApplyModal } from '@components/templates/job/apply/EasyApplyModal';
import { PersonalDataModal } from '@components/templates/job/apply/PersonalDataModal';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { setCookie } from 'cookies-next';
import { getI18nLinkHref } from '@services/i18nPaths';
import { useHeaderInfoData } from '@components/layout/Header/MainNavbar/data/useHeaderInfoData';
import { updateBulkApplyFeed } from '@store/reducers/jobReducer';
import { useAppDispatch, useAppSelector } from '@store/index';
import { showMissingStepsModal } from '@components/templates/job/after-apply/MissingStepsModal';
import { trackEvent } from '@services/tracking';
import { bjToast } from '@components/common';
import { getUserHeaderInfo } from '@store/reducers/userSelector';
import sendGoogleTagEvent from '@hooks/useSendGoogleTagEvent';
import { JOB_APPLY_COOKIE_NAME, JOB_APPLY_WEB_TYPE } from '../../constants';

interface JobTalentActionsHookProps {
  jobSlug: string;
  likedProp?: boolean;
  dislikedProp?: boolean;
  appliedProp: boolean;
  jobId?: string;
  jobTitle?: string;
  company?: string;
  applyCallback?: () => void;
  requestId?: string;
  position?: number;
  placement?: JobPlacement;
  listType?: JobListType;
  hasOwnApplyUrl?: boolean;
  withEasyApply?: boolean;
  allowAfterApply?: boolean;
}

/**
 * @description Job Talent Actions hook. Used for actions that can be triggered by a talent on a job:
 * add to favorites, remove from favorites, add to blocked, remove from blocked, apply to job
 * Can be used in job listing page (cards) or on job detail page.
 *
 * Props:
 * - **jobSlug** - the job slug.
 * - **likedProp** - whether the job is liked or not (the value that comes from API).
 * - **dislikedProp** - whether the job is blocked or not (the value that comes from API).
 * - **appliedProp** - whether the current user has applied to this job (the value that comes from API).
 * - **jobId** - the job ID.
 * - **jobTitle** - the job title.
 * - **company** - the employer name.
 * - **applyCallback** - optional, callback function to be called after apply.
 * - **requestId** - optional, tracking request id. Used for tracking.
 * - **position** - optional, the job position in list. Used for tracking.
 * - **placement** - optional, the job placement (card list / detail page). Used for tracking.
 * - **listType**: optional, the job list type. Used for tracking.
 * - **hasOwnApplyUrl**: optional, whether the job has own apply URL.
 * - **withEasyApply**: optional, whether the Easy Apply functionality is enabled.
 * - **isSinglePage**: optional, whether the job is displayed on a single page.
 *
 * Returns:
 * - **isFavorite** - whether the job is favorite or not (the value from state).
 * - **isBlocked** - whether the job is blocked or not (the value from state).
 * - **isLoadingFavoritesAction** - a boolean flag indicating whether the add / remove job from favorites action is in progress.
 * - **isLoadingBlockedAction** - a boolean flag indicating whether the add / remove job from blocked action is in progress.
 * - **isApplying** - a boolean flag indicating whether the apply action is in progress.
 * - **hasApplied** - a boolean flag indicating that the user has successfully applied.
 * - **addToFavorites** - the function to call to add a job to favorites.
 * - **removeFromFavorites** - the function to call to remove a job from favorites.
 * - **addToBlocked** - the function to call to add a job to blocked.
 * - **removeFromBlocked** - the function to call to remove a job from blocked.
 * - **applyToJob** - the function to call to apply to a job.
 *
 */
export const useJobTalentActions = (props: JobTalentActionsHookProps) => {
  // Destructure props
  const {
    jobSlug, jobId, likedProp, dislikedProp, appliedProp, applyCallback: customApplyCallback,
    requestId, position, placement, listType, jobTitle, company, hasOwnApplyUrl, withEasyApply,
    allowAfterApply = true,
  } = props;

  // Translation
  const { t } = useTranslation('common');

  // NextJs Router
  const { router, locale, params, pathname } = useExtendedRouter();
  const isMcContext = pathname?.includes('/mc');
  const pagesRouter = PagesRouter();

  const headerInfoData = useAppSelector(getUserHeaderInfo);
  const userId = headerInfoData?.userId;

  // Authentication client side
  const { isAuth, redirectToLogin } = useAuthClient();

  // Redux dispatch
  const appDispatch = useAppDispatch();

  // Header info data
  const { forceCheckAuthStatus } = useHeaderInfoData(locale, false);

  // Easy Apply Modal
  const easyApplyModal = useModal(EasyApplyModal);

  let applyToJob: () => void;

  // State
  // ******************************************************
  const [isFavorite, setIsFavorite] = useState(likedProp);
  const [isBlocked, setIsBlocked] = useState(dislikedProp);
  const [hasApplied, setHasApplied] = useState(appliedProp);

  // A flag to indicate whether to start fetching the bulk apply feed
  // It is set to true after every successful application
  const [shouldFetchBulkApplyFeed, setShouldFetchBulkApplyFeed] = useState(false);

  // A flag to indicate whether to show personal data modal
  const [showPersonalDataModal, setShowPersonalDataModal] = useState(false);

  // Sync state when props are updated
  // ******************************************************
  useEffect(() => {
    setIsFavorite(likedProp);
    setIsBlocked(dislikedProp);
    setHasApplied(appliedProp);
    setShouldFetchBulkApplyFeed(false);
  }, [jobSlug, likedProp, dislikedProp, appliedProp]);

  // Callback functions
  // ******************************************************

  // Added to favorites callback
  const addedToFavoritesCallback = () => {
    setIsFavorite(true);

    // When the job is added to favorites, it is also removed from blocked.
    setIsBlocked(false);
  };

  // Removed from favorites callback
  const removedFromFavoritesCallback = () => {
    setIsFavorite(false);
  };

  // Added to blocked callback
  const addedToBlockedCallback = () => {
    setIsBlocked(true);

    // When the job is blocked, it is also removed from favorites.
    setIsFavorite(false);
  };

  // Removed from favorites callback
  const removedFromBlockedCallback = () => {
    setIsBlocked(false);
  };

  // Apply callback functions
  // ******************************************************
  const applyCallback = () => {
    // Update internal state
    setHasApplied(true);

    // Send tracking event of job apply
    trackEvent({
      eventType: 'action',
      entityType: 'job',
      entityId: jobSlug,
      action: 'apply',
      viewContext: placement === JobPlacement.CARD ? 'card' : 'detail',
      ...(userId && { userId: userId.toString() }),
      ...(requestId && { requestId }),
      ...(position && { position: position ?? 0 }),
    });

    // Set GA Job Apply Cookie only if we are redirecting to an external URL.
    // In standard apply, we trigger the event manually below.
    if (hasOwnApplyUrl) {
      const gaValue = buildGaApplyCookieValue(
        jobId,
        jobTitle,
        company,
        placement === JobPlacement.CARD ? 'from_list' : (placement || ''),
        requestId,
      );

      void setCookie(GA_JOB_APPLY_COOKIE_NAME, gaValue, GA_JOB_APPLY_COOKIE_CONFIG);
    }

    // If job has own apply URL, redirect to external application redirect page
    if (hasOwnApplyUrl) {
      const currentPath = typeof window !== 'undefined'
        ? window.location.pathname + window.location.search
        : getI18nLinkHref(`/job/${jobSlug}`, locale);

      if (typeof window !== 'undefined') {
        try {
          sessionStorage.setItem('bj:external-return', currentPath);
        } catch {
          // will not handle this
        }
      }

      // Build redirect page URL WITHOUT `from`
      const target = getI18nLinkHref(
        `/job/${jobSlug}/external-application-redirect`,
        locale,
      );

      router.push(target);
    } else {
      sendGoogleTagEvent('job_apply', 'Custom');

      // Success callback to be used after filling missing steps or after finishing the interview
      const triggerBulkApply = () => {
        setShouldFetchBulkApplyFeed(true);
      };

      showMissingStepsModal({
        jobId: jobId as string,
        jobSlug,
        successCallback: triggerBulkApply,
        noMissingStepsCallback: () => {
          if (allowAfterApply) {
            void router.push(isMcContext ? `/mc/job/${jobSlug}/after-apply` : `/job/${jobSlug}/after-apply`);
          }
        },
      });
    }
  };

  // SWR
  // ******************************************************

  // Add job to favorites
  const {
    trigger: addToFavoritesTrigger,
    isMutating: isAddingToFavorites,
  } = useSwrJobAddToFavorites(jobSlug, locale, addedToFavoritesCallback, t('job.liked.message'));

  // Remove job from favorites
  const {
    trigger: removeFromFavoritesTrigger,
    isMutating: isRemovingFromFavorites,
  } = useSwrJobRemoveFromFavorites(jobSlug, locale, removedFromFavoritesCallback, t('job.unliked.message'));

  // Add job to blocked
  const {
    trigger: addToBlockedTrigger,
    isMutating: isAddingToBlocked,
  } = useSwrJobAddToBlocked(jobSlug, locale, addedToBlockedCallback, t('job.disliked.message'));

  // Remove job from blocked
  const {
    trigger: removeFromBlockedTrigger,
    isMutating: isRemovingFromBlocked,
  } = useSwrJobRemoveFromBlocked(jobSlug, locale, removedFromBlockedCallback, t('job.restored.message'));


  // Apply to job
  const {
    trigger: applyTrigger,
    isMutating: isApplying,
  } = useSwrJobApply({
    slug: jobSlug,
    locale,
    successCallback: () => {
      applyCallback();

      if (customApplyCallback) {
        customApplyCallback();
      }
    },
    errorCallback: (errorObj) => {
      if (!errorObj || !errorObj.errors?.length) {
        return;
      }

      const errorCode = errorObj.errors[0].code;

      if (errorCode === 452 || errorCode === 453) {
        void NiceModal.show(PersonalDataModal, { locale, onUpdateSuccess: () => applyToJob() });
      } else {
        bjToast.error(errorObj.errors[0].message);
      }
    },
    toastsOptions: {
      successToastText: t('job.apply.react.toast.success'),
    },
  });

  // Helpers
  // ******************************************************
  const isLoadingFavoritesAction = useMemo(
    () => isAddingToFavorites || isRemovingFromFavorites,
    [isAddingToFavorites, isRemovingFromFavorites],
  );

  const isLoadingBlockedAction = useMemo(
    () => isAddingToBlocked || isRemovingFromBlocked,
    [isAddingToBlocked, isRemovingFromBlocked],
  );

  // Add to favorites function
  // ******************************************************
  const addToFavorites = () => {
    if (isAuth) {
      void addToFavoritesTrigger();
    } else {
      redirectToLogin();
    }
  };

  // Remove from favorites function
  // ******************************************************
  const removeFromFavorites = () => {
    if (isAuth) {
      void removeFromFavoritesTrigger();
    } else {
      redirectToLogin();
    }
  };

  // Add to blocked function
  // ******************************************************
  const addToBlocked = () => {
    if (isAuth) {
      void addToBlockedTrigger();
    } else {
      redirectToLogin();
    }
  };

  // Remove from blocked function
  // ******************************************************
  const removeFromBlocked = () => {
    if (isAuth) {
      void removeFromBlockedTrigger();
    } else {
      redirectToLogin();
    }
  };

  // Fast apply to job function
  // ******************************************************
  const fastApply = useCallback(() => {
    void applyTrigger({
      requestId,
      pos: position,
      list: listType,
      placement,
      appType: JOB_APPLY_WEB_TYPE,
    });
  }, [applyTrigger, requestId, position, listType, placement]);

  // Apply to job function
  // ******************************************************
  applyToJob = () => {
    if (isAuth) {
      fastApply();
    } else if (withEasyApply) {
      // Open Easy Apply Modal
      easyApplyModal.show({ jobSlug }).then((response) => {
        if (typeof response === 'number' && response) {
          // The API response will create a NextAuth session, so we need to re-check it.
          // If the session is found, the header info data will be updated.
          void forceCheckAuthStatus();

          if (response === 200) {
            setHasApplied(true);

            // Set GA Job Apply Cookie
            const gaValue = buildGaApplyCookieValue(
              jobId,
              jobTitle,
              company,
              placement === JobPlacement.CARD ? 'from_list' : (placement || ''),
              requestId,
            );

            void setCookie(GA_JOB_APPLY_COOKIE_NAME, gaValue, GA_JOB_APPLY_COOKIE_CONFIG);

            // Go the after apply page
            if (allowAfterApply) void router.push(isMcContext ? `/mc/job/${jobSlug}/after-apply` : `/job/${jobSlug}/after-apply`);
          } else if (response === 452 || response === 453) {
            setShowPersonalDataModal(true);
          }
        }
      }).catch(() => {});
    } else {
      // Set cookie for job-apply. By default, it is a session cookie
      void setCookie(JOB_APPLY_COOKIE_NAME, JSON.stringify({
        jobSlug,
        jobId,
        jobTitle,
        company,
        requestId,
        placement: placement === JobPlacement.CARD ? 'from_list' : (placement || ''),
        isMcContext,
      }));

      // Redirect to login page
      redirectToLogin();
    }
  };

  // SWR - Load jobs for bulk apply
  // ***********************************************
  const { data: bulkApplyFeed } = useSwrBulkApplyFeed({
    shouldFetch: shouldFetchBulkApplyFeed,
    jobSlug,
  });

  // Save the bulk apply feed in Redux (to be displayed in Bulk Apply Modal), or redirect to after apply page
  // ***********************************************
  useEffect(() => {
    if (bulkApplyFeed && shouldFetchBulkApplyFeed) {
      if (bulkApplyFeed.items?.length) {
        appDispatch(updateBulkApplyFeed([...bulkApplyFeed.items]));
      }

      setShouldFetchBulkApplyFeed(false);
    }
  }, [bulkApplyFeed, jobSlug, router, shouldFetchBulkApplyFeed, appDispatch, allowAfterApply]);

  // Check if `personalDataModal` query param is set.
  // If true, display the PersonalDataModal.
  // Note that the query param is then removed from URL.
  // ******************************************************
  useEffect(() => {
    if (!pagesRouter) {
      return;
    }

    const { personalDataModal, jobSlug: jobSlugParam, ...otherQueryParams } = pagesRouter?.query || {};

    if (typeof personalDataModal === 'string') {
      setShowPersonalDataModal(true);
      void pagesRouter.replace({ query: { ...otherQueryParams, jobSlug: jobSlugParam } }, { pathname: getI18nLinkHref(`/job/${jobSlug}`, locale), query: otherQueryParams }, { shallow: true });
    }
  }, [pagesRouter, jobSlug, locale, params]);

  useEffect(() => {
    if (showPersonalDataModal) {
      void NiceModal.show(PersonalDataModal, { locale, onUpdateSuccess: fastApply });
    }
  }, [locale, showPersonalDataModal, fastApply]);

  // Return
  // ***********************************************
  return {
    isFavorite,
    isBlocked,
    isLoadingFavoritesAction,
    isLoadingBlockedAction,
    isApplying,
    hasApplied,
    addToFavorites,
    removeFromFavorites,
    addToBlocked,
    removeFromBlocked,
    applyToJob,
    setShouldFetchBulkApplyFeed,
  };
};
